import {
  Box,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import numeral from 'numeral';
import { ReactNode } from 'react';
import Image from 'src/components/image';
import Scrollbar from 'src/components/scrollbar';
import { TablePaginationCustom, useTable } from 'src/components/table';
import { fDate } from 'src/utils/formatTime';
import { Campaign } from '../hooks/useCampaigns';
import { translateKey } from 'src/locales/translate';


interface CampaignsTableProps {
  campaigns: Campaign[];
  isLoading: boolean;
  onRowClick: (campaign: Campaign) => void;
}

interface Row {
  id: string;
  name: string;
  status: string;
  dailyBudget: number;
  totalBudget: number;
  startDate: string;
  endDate: string;
}

interface Column {
  id: keyof Row;
  label: string;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  render?: (row: Row) => ReactNode;
}

const STATUS_COLORS: Record<
  string,
  'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
> = {
  DRAFT: 'default',
  PENDING_PAYMENT: 'warning',
  ACTIVE: 'success',
  PAUSED: 'info',
  COMPLETED: 'primary',
  CANCELLED: 'error',
};

const STATUS_LABELS: Record<string, string> = {
  DRAFT: translateKey("visit.formStatusDraft"),
  PENDING_PAYMENT: translateKey("hardcoded.oczekuje_na_platnosc"),
  ACTIVE: 'Aktywna',
  PAUSED: 'Wstrzymana',
  COMPLETED: translateKey("visit.calendarFilterStatusCompleted"),
  CANCELLED: translateKey("visit.calendarFilterStatusCancelled"),
};

const COLUMNS: Column[] = [
  {
    id: 'name',
    label: 'Nazwa',
    render: (row) => <Typography variant="subtitle2">{row.name}</Typography>,
  },
  {
    id: 'status',
    label: translateKey("visit.tableStatus"),
    render: (row) => (
      <Chip
        size="small"
        label={STATUS_LABELS[row.status] || row.status}
        color={STATUS_COLORS[row.status] || 'default'}
        variant="soft"
      />
    ),
  },
  {
    id: 'dailyBudget',
    label: translateKey("hardcoded.budzet_dzienny"),
    align: 'right',
    render: (row) => `${numeral(row.dailyBudget / 100).format('0,0')} PLN`,
  },
  {
    id: 'totalBudget',
    label: translateKey("hardcoded.budzet_calkowity"),
    align: 'right',
    render: (row) => `${numeral(row.totalBudget / 100).format('0,0')} PLN`,
  },
  {
    id: 'startDate',
    label: translateKey("hardcoded.data_rozpoczecia"),
    render: (row) => fDate(row.startDate),
  },
  {
    id: 'endDate',
    label: translateKey("hardcoded.data_zakonczenia"),
    render: (row) => fDate(row.endDate),
  },
];

export default function CampaignsTable({
  campaigns,
  isLoading,
  onRowClick,
}: CampaignsTableProps): JSX.Element {
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: 25,
  });

  const tableData: Row[] = campaigns.map((c) => ({
    id: c.id,
    name: c.name,
    status: c.status,
    dailyBudget: c.dailyBudget,
    totalBudget: c.totalBudget,
    startDate: c.startDate,
    endDate: c.endDate,
  }));

  const TableHeader = (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          {COLUMNS.map((col) => (
            <TableCell
              key={col.id}
              align={col.align}
              style={{ minWidth: col.minWidth }}
            >
              {col.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    </Table>
  );

  if (isLoading) {
    return (
      <TableContainer
        sx={{ height: 1, display: 'flex', flexDirection: 'column' }}
      >
        {TableHeader}
        <Stack sx={{ p: 3, gap: 1 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={52} />
          ))}
        </Stack>
      </TableContainer>
    );
  }

  if (tableData.length === 0) {
    return (
      <TableContainer
        sx={{ height: 1, display: 'flex', flexDirection: 'column' }}
      >
        {TableHeader}
        <Stack
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          flex="1"
          gap={2}
          sx={{ py: 8 }}
        >
          <Image
            sx={{ width: '200px' }}
            alt="brak kampanii"
            src="/assets/images/empty_table.svg"
          />
          <Typography color="text.secondary">Brak kampanii</Typography>
        </Stack>
      </TableContainer>
    );
  }

  return (
    <>
      <TableContainer
        sx={{ height: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Box
          sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
        >
          <Scrollbar>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align}
                      style={{ minWidth: col.minWidth }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const campaign = campaigns.find((c) => c.id === row.id)!;
                    return (
                      <TableRow
                        hover
                        key={row.id}
                        onClick={() => onRowClick(campaign)}
                        sx={{ cursor: 'pointer' }}
                      >
                        {COLUMNS.map((col) => (
                          <TableCell key={col.id} align={col.align}>
                            {col.render ? col.render(row) : row[col.id]}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Scrollbar>
        </Box>
      </TableContainer>

      <TablePaginationCustom
        count={tableData.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </>
  );
}
